"use client";

import React, { useState } from "react";
import { motion, useReducedMotion } from "framer-motion";
import { trackEvent } from "@/lib/logger-client";
import {
  Mic,
  Building,
  MessageSquare,
  Play,
  Users,
  CheckCircle2,
  Sparkles,
  Calendar,
  Linkedin,
  ArrowRight,
} from "lucide-react";

export function LandingBento() {
  const shouldReduceMotion = useReducedMotion();
  const [activeTab, setActiveTab] = useState<"research" | "questions" | "practice" | "notes">("research");
  const [activeTaskTab, setActiveTaskTab] = useState<"overdue" | "today" | "future">("overdue");
  const [tasks, setTasks] = useState([
    {
      id: "t1",
      category: "overdue",
      company: "Stripe",
      role: "Product Manager",
      title: "Recruiter follow-up call",
      dueText: "2 days overdue",
      completed: false,
    },
    {
      id: "t2",
      category: "overdue",
      company: "Braintree",
      role: "Solutions Engineer",
      title: "Schedule technical screen",
      dueText: "4 days overdue",
      completed: false,
    },
    {
      id: "t3",
      category: "today",
      company: "Ramp",
      role: "PM Lead",
      title: "Technical screen prep",
      dueText: "Today at 5:00 PM",
      completed: false,
    },
    {
      id: "t4",
      category: "today",
      company: "Adyen",
      role: "Senior Developer",
      title: "Submit take-home assignment",
      dueText: "Today at 11:59 PM",
      completed: false,
    },
    {
      id: "t5",
      category: "future",
      company: "OpenAI",
      role: "PM Agent",
      title: "Send thank-you email",
      dueText: "Due tomorrow",
      completed: false,
    },
    {
      id: "t6",
      category: "future",
      company: "Google",
      role: "Frontend Lead",
      title: "Follow up after onsite",
      dueText: "Due in 3 days",
      completed: false,
    },
  ]);

  // Waveform lines for mock interview practice
  const waveformScale = [0.4, 0.85, 0.5, 0.95, 0.65, 0.35, 0.75, 0.5, 0.85, 0.4];

  // Hover transitions
  const cardHover = shouldReduceMotion
    ? {}
    : {
      y: -4,
      boxShadow: "0 12px 30px rgba(20,63,128,0.08)",
      transition: { duration: 0.3, ease: "easeOut" as const },
    };

  return (
    <div className="max-w-5xl mx-auto px-4 space-y-6">

      {/* ROW 1: 3-2 Split (Interview Prep & Career Coaching) */}
      <div className="grid grid-cols-1 lg:grid-cols-5 gap-6 items-stretch">

        {/* Card 1: Interview Prep (3/5 cols) */}
        <motion.div
          whileHover={cardHover}
          className="lg:col-span-3 flex flex-col justify-between rounded-2xl p-5 md:p-6 relative overflow-hidden group select-none bg-white border border-slate-200"
          style={{ boxShadow: "0 4px 24px rgba(20,63,128,0.04)" }}
        >
          {/* Top text block */}
          <div className="mb-4">
            <div className="flex items-center gap-2 mb-1.5">
              <span className="px-2.5 py-0.5 rounded-full text-xs font-semibold bg-[#143f80]/10 text-[#143f80]">
                Pre-Interview Agent
              </span>
            </div>
            <h3 className="text-xl md:text-2xl font-bold mb-1 text-[#143f80]">
              Interview Prep
            </h3>
            <p className="text-sm text-slate-600 leading-relaxed max-w-[50ch]">
              Recreated from the actual app. Toggle the tabs below to explore the AI-generated research, practice questions, and notes.
            </p>
          </div>

          {/* Interactive Tabbed UI Preview */}
          <div className="bg-slate-50/50 rounded-xl border border-slate-200/60 p-3.5 w-full relative z-10 flex flex-col flex-1 min-h-[290px] justify-between">

            {/* Header & Simulated Tabs */}
            <div className="space-y-2">
              <div className="flex items-center justify-between px-1">
                <span className="text-xs font-bold text-[#143f80] uppercase tracking-wide">
                  Interview Dashboard
                </span>
                <span className="text-xs bg-[#143f80]/10 text-[#143f80] font-bold px-2 py-0.5 rounded">
                  Target: Stripe
                </span>
              </div>

              {/* Tab Bar - Replicated from actual app */}
              <div className="grid grid-cols-4 bg-slate-200/60 rounded-full p-0.5 gap-0.5">
                {[
                  { id: "research" as const, label: "Research", icon: Building },
                  { id: "questions" as const, label: "Questions", icon: MessageSquare },
                  { id: "practice" as const, label: "Practice", icon: Play },
                  { id: "notes" as const, label: "Notes", icon: Users },
                ].map((tab) => {
                  const Icon = tab.icon;
                  const isActive = activeTab === tab.id;
                  return (
                    <button
                      key={tab.id}
                      onClick={() => {
                        setActiveTab(tab.id);
                        trackEvent("bento_tab_click", {
                          card: "interview_prep",
                          tab: tab.id,
                        });
                      }}
                      className={`relative py-1 px-1 rounded-full text-xs font-bold transition-all flex items-center justify-center gap-1 ${isActive
                        ? "bg-[#143f80] text-white shadow-sm"
                        : "text-slate-500 hover:text-slate-800"
                        }`}
                    >
                      <Icon className="w-3.5 h-3.5 shrink-0" />
                      <span className="hidden sm:inline">{tab.label}</span>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Tab content area (Flex-grow to utilize available height) */}
            <div className="flex-1 my-3 overflow-y-auto min-h-[145px] pr-0.5">

              {/* RESEARCH TAB */}
              {activeTab === "research" && (
                <div className="space-y-2.5 text-left">
                  <div className="grid grid-cols-2 gap-2.5">
                    <div className="bg-white p-2.5 rounded-lg border border-slate-200/60">
                      <span className="text-xs uppercase tracking-wider text-slate-400 font-bold block mb-0.5">
                        Mission & Focus
                      </span>
                      <p className="text-xs text-slate-700 leading-snug">
                        &quot;Increase the GDP of the internet&quot; through financial infrastructure.
                      </p>
                    </div>
                    <div className="bg-white p-2.5 rounded-lg border border-slate-200/60">
                      <span className="text-xs uppercase tracking-wider text-slate-400 font-bold block mb-0.5">
                        Competitors
                      </span>
                      <p className="text-xs text-slate-700 leading-snug">
                        Adyen, PayPal, Checkout.com, Braintree.
                      </p>
                    </div>
                  </div>
                  <div className="bg-blue-50/70 p-2.5 rounded-lg border border-blue-100">
                    <span className="text-xs uppercase tracking-wider text-[#143f80]/70 font-bold block mb-0.5">
                      Strategic Interview Tip
                    </span>
                    <p className="text-xs text-[#143f80] font-semibold leading-relaxed">
                      Emphasize API scalability. Focus on developer enablement and global transaction compliance.
                    </p>
                  </div>
                </div>
              )}

              {/* QUESTIONS TAB */}
              {activeTab === "questions" && (
                <div className="space-y-2.5 text-left">
                  <div className="bg-white p-2.5 rounded-lg border border-slate-200/60 space-y-1.5">
                    <p className="text-xs font-bold text-slate-800 leading-snug">
                      &quot;Can you share an experience where you had to adapt to a sudden shift in project requirements?&quot;
                    </p>
                    <div className="border-t border-slate-100 pt-1.5 space-y-0.5">
                      <span className="text-xs uppercase tracking-wider text-slate-400 font-bold block">
                        AI Recommended Story:
                      </span>
                      <p className="text-xs text-slate-600 leading-relaxed">
                        Restructured the data-fetching layer mid-sprint to GraphQL. Delivered 2 days early and cut latency by 35%.
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* PRACTICE TAB */}
              {activeTab === "practice" && (
                <div className="space-y-2.5 text-left">
                  <div className="bg-white p-2.5 rounded-lg border border-slate-200/60 flex items-start gap-2">
                    <div className="w-5 h-5 rounded-full bg-[#143f80]/10 flex items-center justify-center text-xs font-bold text-[#143f80] shrink-0 mt-0.5">
                      AI
                    </div>
                    <p className="text-xs text-slate-700 leading-relaxed font-medium">
                      &quot;How do you manage prioritization when handling multiple stakeholders with conflicting requirements?&quot;
                    </p>
                  </div>

                  <div className="border border-[#143f80]/20 bg-[#f0f6ff]/70 rounded-lg p-2 flex flex-col items-center justify-center space-y-1">
                    <div className="flex items-center gap-1.5">
                      <Mic className="w-3.5 h-3.5 text-[#143f80] animate-pulse" />
                      <span className="text-xs font-bold text-[#143f80]">Recording Response...</span>
                    </div>

                    {/* Animated Waveform */}
                    <div className="flex items-center gap-1 h-4">
                      {waveformScale.map((scale, i) => (
                        <motion.div
                          key={i}
                          className="w-[2px] bg-[#143f80] rounded-full"
                          animate={
                            shouldReduceMotion
                              ? { height: "4px" }
                              : { height: ["4px", `${scale * 12}px`, "4px"] }
                          }
                          transition={{
                            duration: 1.2,
                            repeat: Infinity,
                            delay: i * 0.1,
                            ease: "easeInOut",
                          }}
                          style={{ height: "4px" }}
                        />
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {/* NOTES TAB */}
              {activeTab === "notes" && (
                <div className="space-y-2 text-left">
                  <div className="border border-slate-200/60 rounded-lg p-2 bg-white">
                    <div className="flex items-center justify-between mb-0.5">
                      <span className="text-xs font-bold text-slate-800">Round 1: Recruiter Call</span>
                      <span className="text-xs bg-slate-100 text-slate-700 font-bold px-1.5 py-0.5 rounded">
                        Completed
                      </span>
                    </div>
                    <p className="text-xs text-slate-500 leading-snug">
                      Recruiter loved our payments background. Focus on latency for next round.
                    </p>
                  </div>

                  <div className="border border-blue-100 rounded-lg p-2 bg-blue-50/40">
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-xs font-bold text-[#143f80]">Round 2: Technical Panel</span>
                      <span className="text-xs bg-blue-100 text-[#143f80] font-bold px-1.5 py-0.5 rounded">
                        Active
                      </span>
                    </div>
                    <div className="space-y-1">
                      <div className="flex items-center gap-1.5 text-xs text-slate-500">
                        <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500 shrink-0" />
                        <span className="line-through">Review API developer docs</span>
                      </div>
                      <div className="flex items-center gap-1.5 text-xs text-slate-700">
                        <div className="w-3.5 h-3.5 rounded-full border border-blue-500 flex items-center justify-center shrink-0">
                          <div className="w-1.5 h-1.5 bg-blue-500 rounded-full" />
                        </div>
                        <span className="font-semibold text-slate-800">Run voice mock practice questions</span>
                      </div>
                    </div>
                  </div>
                </div>
              )}

            </div>

            {/* Bottom active feedback panel */}
            <div className="bg-slate-100/80 rounded-lg p-2 flex items-center justify-between border border-slate-200/40 text-xs shrink-0">
              <span className="text-slate-500 font-medium">
                Try clicking other tabs to see different mockups.
              </span>

            </div>

          </div>
        </motion.div>

        {/* Card 2: Career Coaching (2/5 cols) */}
        <motion.div
          whileHover={cardHover}
          className="lg:col-span-2 flex flex-col justify-between rounded-2xl p-5 md:p-6 relative overflow-hidden select-none bg-white border border-slate-200"
          style={{ boxShadow: "0 4px 24px rgba(20,63,128,0.04)" }}
        >
          <div className="mb-4">
            <span className="inline-block px-2.5 py-0.5 rounded-full text-xs font-semibold bg-[#143f80]/10 text-[#143f80] mb-2">
              Career Agent
            </span>
            <h3 className="text-lg font-bold mb-1 text-[#143f80]">
              Career Coaching
            </h3>
            <p className="text-sm text-slate-500 leading-relaxed">
              Finds the professional, metric-driven stories hidden in your work history.
            </p>
          </div>

          {/* Interactive Coaching Conversation to Resume Bullet Visual (Fills card height) */}
          <div className="bg-slate-50 rounded-xl p-2.5 border border-slate-200/60 flex-1 flex flex-col justify-between mt-2 gap-2.5">
            {/* AI Coach Question 1 */}
            <div className="flex items-start gap-2 text-left">
              <div className="w-5 h-5 rounded-full bg-[#143f80]/10 flex items-center justify-center text-xs font-bold text-[#143f80] shrink-0 mt-0.5">
                C
              </div>
              <p className="text-xs text-slate-700 leading-normal italic font-medium bg-white px-2.5 py-1 rounded-lg rounded-tl-none border border-slate-100 shadow-sm max-w-[90%]">
                &quot;What was a database project you led at Stripe?&quot;
              </p>
            </div>

            {/* User Answer 1 */}
            <div className="flex items-start gap-2 justify-end text-right">
              <p className="text-xs text-slate-600 leading-normal italic bg-slate-200/40 px-2.5 py-1 rounded-lg rounded-tr-none max-w-[85%]">
                &quot;I optimized index scripts and replication.&quot;
              </p>
            </div>

            {/* AI Coach Question 2 */}
            <div className="flex items-start gap-2 text-left">
              <div className="w-5 h-5 rounded-full bg-[#143f80]/10 flex items-center justify-center text-xs font-bold text-[#143f80] shrink-0 mt-0.5">
                C
              </div>
              <p className="text-xs text-slate-700 leading-normal italic font-medium bg-white px-2.5 py-1 rounded-lg rounded-tl-none border border-slate-100 shadow-sm max-w-[90%]">
                &quot;Great! What was the impact on performance or the team?&quot;
              </p>
            </div>

            {/* User Answer 2 */}
            <div className="flex items-start gap-2 justify-end text-right">
              <p className="text-xs text-slate-600 leading-normal italic bg-slate-200/40 px-2.5 py-1 rounded-lg rounded-tr-none max-w-[85%]">
                &quot;It cut load times in half and saved developers 15 hours a week.&quot;
              </p>
            </div>

            {/* AI Story Extraction Arrow / Divider */}
            <div className="flex items-center justify-center gap-2 my-1">
              <div className="h-[1px] bg-slate-200 flex-1" />
              <span className="text-xs font-bold text-amber-600 bg-amber-50 px-2 py-0.5 rounded flex items-center gap-1 uppercase tracking-wider shrink-0">
                <Sparkles className="w-3.5 h-3.5" /> Story Extraction
              </span>
              <div className="h-[1px] bg-slate-200 flex-1" />
            </div>

            {/* Polished Result Added to Master Profile */}
            <div className="bg-emerald-50/60 border border-emerald-100 rounded-lg p-2 text-left">
              <span className="text-xs font-bold text-emerald-700 block mb-0.5">
                ✦ Added to Master Profile:
              </span>
              <p className="text-xs text-slate-700 leading-normal font-semibold">
                &quot;Refactored database query indexing to reduce page load latency by 50% and save 15 hours of developer time weekly.&quot;
              </p>
            </div>
          </div>
        </motion.div>

      </div>

      {/* ROW 2: 2-3 Split (LinkedIn & Outreach & Milestones) */}
      <div className="grid grid-cols-1 lg:grid-cols-5 gap-6 items-stretch">

        {/* Card 3: LinkedIn & Outreach (2/5 cols) */}
        <motion.div
          whileHover={cardHover}
          className="lg:col-span-2 flex flex-col justify-between rounded-2xl p-5 md:p-6 relative overflow-hidden select-none bg-white border border-slate-200"
          style={{ boxShadow: "0 4px 24px rgba(20,63,128,0.04)" }}
        >
          <div className="mb-4">
            <span className="inline-block px-2.5 py-0.5 rounded-full text-xs font-semibold bg-[#143f80]/5 text-[#143f80] mb-2">
              Profile Optimization
            </span>
            <h3 className="text-lg font-bold mb-1 text-[#143f80]">
              LinkedIn & Outreach
            </h3>
            <p className="text-sm text-slate-500 leading-relaxed">
              Profile rewrite. Networking messages that don&apos;t sound like templates.
            </p>
          </div>

          {/* LinkedIn Message Preview */}
          <div className="bg-slate-50/60 rounded-xl p-3 border border-slate-200/60 flex flex-col justify-between flex-1 min-h-[125px]">
            <div className="space-y-2">
              <div className="flex items-center gap-1.5 pb-1.5 border-b border-slate-200">
                <Linkedin className="w-4 h-4 text-[#0a66c2] shrink-0" />
                <span className="text-xs font-bold text-slate-500 uppercase tracking-wide">Recruiter Outreach</span>
              </div>
              <p className="text-xs text-slate-600 italic leading-relaxed mt-1">
                &quot;Hi Sarah, I noticed you&apos;re expanding your platform engineering team. Based on my experience building scalable payment integrations at Stripe, I&apos;d love to learn more about the challenges you&apos;re solving.&quot;
              </p>
            </div>
            <div className="flex items-center justify-between text-[11px] text-slate-400 mt-2 pt-1.5 border-t border-slate-200/60">
              <span>Tone: Professional & Warm</span>
              <span className="text-[#0a66c2] font-semibold">Ready to send</span>
            </div>
          </div>
        </motion.div>

        {/* Card 4: Milestones & Follow-ups (3/5 cols) */}
        <motion.div
          whileHover={cardHover}
          className="lg:col-span-3 flex flex-col justify-between rounded-2xl p-5 md:p-6 relative overflow-hidden select-none bg-white border border-slate-200"
          style={{ boxShadow: "0 4px 24px rgba(20,63,128,0.04)" }}
        >
          <div className="mb-4">
            <span className="inline-block px-2.5 py-0.5 rounded-full text-xs font-semibold bg-[#059669]/10 text-[#059669] mb-2">
              Automation
            </span>
            <h3 className="text-lg font-bold mb-1 text-[#059669]">
              Milestones & Follow-ups
            </h3>
            <p className="text-sm text-slate-500 leading-relaxed">
              Consolidates and automates follow-ups from all applications in one dashboard.
            </p>
          </div>

          {/* Interactive Task Board Preview */}
          <div className="bg-slate-50/50 rounded-xl p-3.5 border border-slate-200/60 flex flex-col justify-between flex-1 min-h-[220px] w-full text-left relative z-10">
            {/* Header & Tabs */}
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 mb-3 shrink-0">
              <span className="text-xs font-bold text-[#059669] uppercase tracking-wide">
                Task Tracker
              </span>
              <div className="flex bg-slate-200/60 rounded-full p-0.5 gap-0.5 self-start sm:self-auto">
                {[
                  { id: "overdue" as const, label: "Overdue", color: "hover:text-red-600", activeColor: "bg-red-600 text-white shadow-sm" },
                  { id: "today" as const, label: "Today", color: "hover:text-amber-600", activeColor: "bg-amber-500 text-white shadow-sm" },
                  { id: "future" as const, label: "Future", color: "hover:text-emerald-600", activeColor: "bg-emerald-600 text-white shadow-sm" },
                ].map((tab) => {
                  const isActive = activeTaskTab === tab.id;
                  const count = tasks.filter(t => t.category === tab.id && !t.completed).length;
                  return (
                    <button
                      key={tab.id}
                      onClick={() => {
                        setActiveTaskTab(tab.id);
                        trackEvent("bento_tab_click", {
                          card: "milestones",
                          tab: tab.id,
                        });
                      }}
                      className={`relative py-1 px-2.5 rounded-full text-xs font-bold transition-all flex items-center gap-1 ${isActive
                        ? tab.activeColor
                        : `text-slate-500 ${tab.color}`
                        }`}
                    >
                      <span>{tab.label}</span>
                      {count > 0 && (
                        <span className={`w-4 h-4 rounded-full flex items-center justify-center text-[10px] font-extrabold ${isActive ? "bg-white text-slate-900" : "bg-slate-200 text-slate-700"
                          }`}>
                          {count}
                        </span>
                      )}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Task list container */}
            <div className="flex-1 overflow-y-auto space-y-2 min-h-[120px] pr-1">
              {tasks.filter(t => t.category === activeTaskTab).length === 0 ? (
                <div className="flex flex-col items-center justify-center h-full text-slate-400 py-4">
                  <CheckCircle2 className="w-8 h-8 text-emerald-500 mb-1" />
                  <p className="text-xs font-medium">All caught up!</p>
                </div>
              ) : (
                tasks
                  .filter(t => t.category === activeTaskTab)
                  .map((task) => {
                    const isCompleted = task.completed;

                    // Category styling
                    let bgStyle = "bg-white border-slate-200";
                    let textStyle = "text-slate-800";
                    let dueColor = "";

                    if (isCompleted) {
                      bgStyle = "bg-slate-100/50 border-slate-200 opacity-60";
                      textStyle = "text-slate-400 line-through";
                      dueColor = "text-slate-400";
                    } else {
                      if (task.category === "overdue") {
                        bgStyle = "bg-red-50/50 border-red-100 hover:bg-red-50";
                        dueColor = "text-red-600 font-bold";
                      } else if (task.category === "today") {
                        bgStyle = "bg-amber-50/50 border-amber-100 hover:bg-amber-50";
                        dueColor = "text-amber-600 font-semibold";
                      } else {
                        bgStyle = "bg-emerald-50/50 border-emerald-100 hover:bg-emerald-50";
                        dueColor = "text-emerald-600 font-semibold";
                      }
                    }

                    return (
                      <div
                        key={task.id}
                        onClick={() => {
                          const targetTask = tasks.find(t => t.id === task.id);
                          const nextCompleted = !targetTask?.completed;
                          setTasks(prev =>
                            prev.map(t =>
                              t.id === task.id ? { ...t, completed: nextCompleted } : t
                            )
                          );
                          trackEvent("bento_task_toggle", {
                            task_id: task.id,
                            company: task.company,
                            role: task.role,
                            title: task.title,
                            completed: nextCompleted,
                          });
                        }}
                        className={`flex items-center justify-between p-2.5 rounded-xl border transition-all duration-200 cursor-pointer ${bgStyle}`}
                      >
                        <div className="flex items-center gap-2.5 min-w-0 flex-1">
                          {/* Checkbox */}
                          <div className="shrink-0">
                            {isCompleted ? (
                              <CheckCircle2 className="w-4 h-4 text-emerald-500 shrink-0" />
                            ) : (
                              <div className={`w-4 h-4 rounded-full border flex items-center justify-center shrink-0 ${task.category === "overdue" ? "border-red-400" :
                                task.category === "today" ? "border-amber-400" : "border-emerald-400"
                                }`}>
                                <div className={`w-1.5 h-1.5 rounded-full ${task.category === "overdue" ? "bg-red-500" :
                                  task.category === "today" ? "bg-amber-500" : "bg-emerald-500"
                                  }`} />
                              </div>
                            )}
                          </div>

                          {/* Task details */}
                          <div className="min-w-0 flex-1">
                            <div className="flex items-center gap-1.5">
                              <span className={`text-xs font-extrabold truncate ${isCompleted ? "text-slate-400" : "text-slate-700"}`}>
                                {task.company}
                              </span>
                              <span className="text-[11px] text-slate-400">•</span>
                              <span className="text-xs text-slate-500 truncate">
                                {task.role}
                              </span>
                            </div>
                            <p className={`text-sm font-semibold leading-tight truncate mt-0.5 ${textStyle}`}>
                              {task.title}
                            </p>
                          </div>
                        </div>

                        {/* Due status */}
                        <span className={`text-xs shrink-0 ml-2 ${dueColor}`}>
                          {isCompleted ? "Completed" : task.dueText}
                        </span>
                      </div>
                    );
                  })
              )}
            </div>

            {/* Footer active feedback panel */}
            <div className="bg-slate-100/80 rounded-lg p-2 flex items-center justify-between border border-slate-200/40 text-xs mt-3 shrink-0">
              <span className="text-slate-500 font-medium">
                Tap tasks to mark them complete and update your dashboard.
              </span>

            </div>
          </div>
        </motion.div>

      </div>

    </div>
  );
}
